import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Box, Paper, Typography, Button, Alert, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Select, MenuItem, FormControl, InputLabel,
  Link as MuiLink, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Download as DownloadIcon, Forum as ForumIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Reply as ReplyIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useFlow } from '../contexts/FlowContext';
import { deliveryService } from '../services/deliveryService';
import DestinationDialog from '../components/deliveries/DestinationDialog';
import { track } from '../utils/analytics';

const PAGE_SIZE = 50;
/** Per-environment, so a second environment's first submission is still its own funnel moment. */
const firstSubmissionKey = (envId) => `flowgate.firstSubmissionReceived.${envId}`;

const when = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/**
 * Submissions, every submission of every flow, across the environment, newest first.
 * Unlike Leads (Click-to-WhatsApp attribution, gated behind AD_ATTRIBUTION_ENABLED) this page
 * is always visible: it's the honest record of "what customers said", independent of ads.
 * Data: GET /api/environments/{envId}/submissions (workstream A ↔ G contract).
 */
const SubmissionsPage = () => {
  const theme = useTheme();
  // Same test the rest of the app uses for "this is a phone": a table with six columns is not
  // one of the things that fits.
  const narrow = useMediaQuery(theme.breakpoints.down('md'));

  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { flows } = useFlow();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowFilter, setFlowFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [deliveryDialogFlow, setDeliveryDialogFlow] = useState(null);

  const envId = selectedEnvironment?.id;

  const load = useCallback(async (targetPage = 0, append = false) => {
    if (!envId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await deliveryService.listSubmissions(
        envId, { flowId: flowFilter || undefined, page: targetPage, size: PAGE_SIZE }, authenticatedFetch,
      );
      setRows((prev) => (append ? [...prev, ...(result.items || [])] : (result.items || [])));
      setTotal(result.total || 0);
      setPage(targetPage);
      // Launch funnel event (docs/launch/ws-I.md): the first time this env's Submissions page
      // shows a real row. Idempotent per env via localStorage, no props, no answers, no
      // contact, this only marks that the moment happened.
      if ((result.total || 0) > 0) {
        try {
          const key = firstSubmissionKey(envId);
          if (!window.localStorage.getItem(key)) {
            window.localStorage.setItem(key, '1');
            track('first_submission_received');
          }
        } catch {
          // Private browsing or storage disabled, never break the page over this.
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId, flowFilter]);

  useEffect(() => { load(0, false); }, [load]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedFlow = useMemo(
    () => (flows || []).find((f) => f.id === flowFilter) || null,
    [flows, flowFilter],
  );

  const downloadCsv = () => {
    const answerKeys = [...new Set(rows.flatMap((r) => Object.keys(r.answers || {})))];
    const headers = ['flowName', 'contact', 'submittedAt', ...answerKeys];
    const lines = [headers.map(csvEscape).join(',')];
    for (const r of rows) {
      lines.push([
        r.flowName, r.contact, r.submittedAt,
        ...answerKeys.map((k) => (r.answers || {})[k]),
      ].map(csvEscape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'submissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
      {/* Wraps rather than pushing the page sideways: at 360px the heading and two buttons do
          not share a line, and a page that scrolls horizontally takes its own header with it
          (reported 2026-09-20). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <ForumIcon color="primary" />
        <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1, minWidth: 0 }}>
          Submissions
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load(0, false)} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every submission of every flow, in one place. Reply straight into the WhatsApp
        conversation, or set up where answers should go automatically.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel id="submissions-flow-filter-label">Filter by flow</InputLabel>
          <Select
            labelId="submissions-flow-filter-label"
            label="Filter by flow"
            value={flowFilter}
            onChange={(e) => setFlowFilter(e.target.value)}
            inputProps={{ 'data-testid': 'submissions-flow-filter' }}
          >
            <MenuItem value="">All flows</MenuItem>
            {(flows || []).map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedFlow && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setDeliveryDialogFlow(selectedFlow)}
            data-testid="deliver-answers-button"
          >
            Deliver answers…
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!envId ? (
        <Alert severity="info">Select an environment to see its submissions.</Alert>
      ) : loading && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No submissions yet. Answers appear here once a flow is live and a customer completes it.{' '}
          <MuiLink component={RouterLink} to="/flows">Go to your flows</MuiLink>.
        </Alert>
      ) : (
        <>
          {narrow ? (
            /* A six-column table does not fit a phone, and making the page scroll sideways to
               hold one takes the header off the screen with it. One card per submission instead:
               who, when, what they said, and the reply button at thumb width. */
            <Stack spacing={1.5} className="ph-no-capture">
              {rows.map((r) => {
                const entries = Object.entries(r.answers || {});
                const isExpanded = expanded.has(r.id);
                const shown = isExpanded ? entries : entries.slice(0, 2);
                return (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.75 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>{r.contact}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {r.flowName} · {when(r.submittedAt)}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplyIcon />}
                        component="a"
                        href={r.replyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!r.replyLink}
                        sx={{ flexShrink: 0 }}
                      >
                        Reply
                      </Button>
                    </Stack>

                    {entries.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No answers recorded</Typography>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        {shown.map(([k, v]) => (
                          <Typography key={k} variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                            <b>{k}:</b> {String(v)}
                          </Typography>
                        ))}
                        {entries.length > 2 && (
                          <Button
                            size="small"
                            onClick={() => toggleExpanded(r.id)}
                            data-testid={`expand-answers-${r.id}`}
                            sx={{ mt: 0.5, px: 0 }}
                          >
                            {isExpanded ? 'Show less' : `Show ${entries.length - 2} more`}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          ) : (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Flow</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Answers</TableCell>
                  <TableCell align="right">Reply</TableCell>
                </TableRow>
              </TableHead>
              {/* Customer-supplied content (contact, submitted answers), excluded from session
                  recordings, including the collapsed-answers row below. */}
              <TableBody className="ph-no-capture">
                {rows.map((r) => {
                  const answers = r.answers || {};
                  const entries = Object.entries(answers);
                  const isExpanded = expanded.has(r.id);
                  const preview = entries.slice(0, 2);
                  const restCount = entries.length - preview.length;
                  return (
                    <React.Fragment key={r.id}>
                      <TableRow hover>
                        <TableCell sx={{ width: 40 }}>
                          {entries.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(r.id)}
                              aria-label={isExpanded ? 'Collapse answers' : 'Expand answers'}
                              data-testid={`expand-answers-${r.id}`}
                            >
                              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{r.flowName}</TableCell>
                        <TableCell>{r.contact}</TableCell>
                        <TableCell><Typography variant="caption">{when(r.submittedAt)}</Typography></TableCell>
                        <TableCell>
                          {entries.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          ) : (
                            <Typography variant="caption">
                              {preview.map(([k, v]) => `${k}: ${v}`).join(' · ')}
                              {restCount > 0 && !isExpanded ? ` (+${restCount} more)` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReplyIcon />}
                            component="a"
                            href={r.replyLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!r.replyLink}
                          >
                            Reply
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && entries.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 1 }}>
                            <Box sx={{ pl: 5 }}>
                              {entries.map(([k, v]) => (
                                <Typography key={k} variant="caption" display="block">
                                  <b>{k}:</b> {String(v)}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
          )}
          {rows.length < total && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button size="small" onClick={() => load(page + 1, true)} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : `Load more (${rows.length} of ${total})`}
              </Button>
            </Box>
          )}
        </>
      )}

      <DestinationDialog
        open={!!deliveryDialogFlow}
        onClose={() => setDeliveryDialogFlow(null)}
        flow={deliveryDialogFlow}
      />
    </Box>
  );
};

export default SubmissionsPage;
